import config from '@/config';
import { request, uploadFile } from '@/utils/request.js';

// 生成一个极短的WAV格式静音音频Blob URL（用于解锁浏览器播放权限）
function createSilentWavUrl() {
    const sampleRate = 8000;
    const numSamples = 1;
    const buffer = new ArrayBuffer(44 + numSamples * 2);
    const view = new DataView(buffer);
    // WAV文件头
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + numSamples * 2, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(view, 36, 'data');
    view.setUint32(40, numSamples * 2, true);
    // 写入一个静音采样
    view.setInt16(44, 0, true);
    return URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
}

// 向DataView写入字符串（WAV头专用）
function writeString(view, offset, str) {
    for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
    }
}

/**
 * 音频功能 Mixin（H5）
 * 播放：原生Audio + 静音WAV解锁自动播放限制
 * 录音：getUserMedia采集PCM → 降采样16kHz → 编码WAV → 上传ASR识别
 */
export default {
    data() {
        return {
            audioIsRecording: false, // 是否正在录音
            audioRecordTime: 0, // 录音时长（秒）
            audioRecordTimer: null, // 录音计时器
            audioMaxRecordTime: 60, // 最大录音时长（秒）
            audioEl: null, // 原生Audio元素
            audioCtx: null, // AudioContext引用（用于解锁iOS）
            audioUnlocked: false, // 音频自动播放是否已解锁
            audioPendingPlay: null, // 被浏览器阻止后待重播的音频信息
            audioSilentUrl: null, // 静音音频Blob URL
            audioH5Stream: null, // 录音时的麦克风MediaStream
            audioH5RecordCtx: null, // 录音专用AudioContext
            audioH5Processor: null, // ScriptProcessorNode
            audioH5Source: null, // MediaStreamSource
            audioH5PcmData: [], // 录制的PCM数据缓冲
        };
    },

    methods: {
        // 初始化音频播放器（原生Audio元素）
        audioInitPlayer() {
            try {
                const el = new Audio();
                el.setAttribute('preload', 'auto');
                el.setAttribute('playsinline', '');
                el.setAttribute('webkit-playsinline', '');
                el.volume = 1.0;
                this.audioEl = el;
                // 预生成静音音频URL
                this.audioSilentUrl = createSilentWavUrl();
            } catch (e) {
                console.error('[audio] 创建Audio元素失败:', e);
            }
        },

        // 用户交互时调用：解锁浏览器音频自动播放限制
        audioUnlock() {
            if (this.audioUnlocked) {
                this._audioRetryPending();
                return;
            }
            // 正在播报说明播放权限正常，跳过静音解锁（换src会打断当前播报）
            if (this.isSpeaking) return;
            if (!this.audioEl) return;

            // 方式1：恢复AudioContext（iOS Safari需要）
            this._resumeAudioContext();

            // 方式2：播放静音WAV来激活Audio元素的播放权限
            this.audioEl.src = this.audioSilentUrl;
            this.audioEl.loop = false;
            const playPromise = this.audioEl.play();
            if (playPromise && typeof playPromise.then === 'function') {
                playPromise
                    .then(() => {
                        this.audioEl.pause();
                        this.audioEl.currentTime = 0;
                        this.audioUnlocked = true;
                        console.log('[audio] 音频播放权限已解锁');
                        this._audioRetryPending();
                    })
                    .catch((err) => {
                        console.warn(
                            '[audio] 解锁失败:',
                            err.name,
                            err.message
                        );
                        // 作为最后手段：尝试通过AudioContext解锁
                        this._unlockViaAudioContext();
                    });
            }
        },

        // 通过AudioContext方式解锁（兼容方案）
        _unlockViaAudioContext() {
            try {
                const AC = window.AudioContext || window.webkitAudioContext;
                if (AC) {
                    if (!this.audioCtx) {
                        this.audioCtx = new AC();
                    }
                    const ctx = this.audioCtx;
                    if (ctx.state === 'suspended') {
                        ctx.resume()
                            .then(() => {
                                this.audioUnlocked = true;
                                console.log('[audio] 通过AudioContext解锁成功');
                                this._audioRetryPending();
                            })
                            .catch(() => {});
                    } else {
                        this.audioUnlocked = true;
                        this._audioRetryPending();
                    }
                }
            } catch (e) {
                // 忽略
            }
        },

        // 恢复可能被浏览器挂起的AudioContext
        _resumeAudioContext() {
            try {
                const AC = window.AudioContext || window.webkitAudioContext;
                if (AC) {
                    if (!this.audioCtx) {
                        this.audioCtx = new AC();
                    }
                    if (this.audioCtx.state === 'suspended') {
                        this.audioCtx.resume().catch(() => {});
                    }
                }
            } catch (e) {
                // 忽略
            }
        },

        // 重播被浏览器阻止的音频
        _audioRetryPending() {
            if (this.audioPendingPlay) {
                const { audioUrl, onEnded } = this.audioPendingPlay;
                this.audioPendingPlay = null;
                this.audioPlay(audioUrl, onEnded);
            }
        },

        // 切换语音/键盘输入模式
        audioToggleInputMode() {
            this.chatIsVoiceMode = !this.chatIsVoiceMode;
            // 切到语音模式只做播放解锁，不预检麦克风：
            // getUserMedia 激活麦克风会触发浏览器回声消除把媒体音量压低几秒，打断正在播报的语音
            // 麦克风能力/权限在长按录音（audioStartRecord）时校验兜底
            if (this.chatIsVoiceMode) {
                this.audioUnlock();
            }
            if (this.audioIsRecording) {
                this.audioCancelRecord();
            }
        },

        // 开始录音（防重入 + 代际序号：松手后迟到的麦克风启动会自动作废清理）
        async audioStartRecord() {
            if (this.audioIsRecording) return;
            if (
                !navigator.mediaDevices ||
                !navigator.mediaDevices.getUserMedia
            ) {
                this.$toast('当前环境不支持录音');
                // 弹回键盘模式，避免停留在无法使用的语音模式
                this.chatIsVoiceMode = false;
                return;
            }
            // 按下录音即停掉正在播放的语音，避免边外放边录音
            this.audioStop();
            // 按下瞬间立即显示录音状态并启动计时（计按住时长，不依赖麦克风就绪）
            this.audioIsRecording = true;
            this.audioRecordTime = 0;
            // 代际序号：每次开始录音递增，松手/取消后序号不匹配的异步启动直接作废
            this._audioRecordSeq = (this._audioRecordSeq || 0) + 1;
            const seq = this._audioRecordSeq;
            clearInterval(this.audioRecordTimer);
            this.audioRecordTimer = setInterval(() => {
                this.audioRecordTime++;
                if (this.audioRecordTime >= this.audioMaxRecordTime) {
                    this.audioStopRecord();
                }
            }, 1000);
            // 关键：AudioContext 必须在用户手势内同步创建并 resume，
            // 放在 await 之后会脱离手势上下文，iOS 上会 suspended 导致采不到音
            const AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) {
                this.$toast('当前浏览器不支持录音');
                this.audioCancelRecord();
                return;
            }
            this.audioH5RecordCtx = new AC();
            if (this.audioH5RecordCtx.state === 'suspended') {
                this.audioH5RecordCtx.resume().catch(() => {});
            }
            try {
                this.audioH5Stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        channelCount: 1,
                        echoCancellation: true,
                        noiseSuppression: true,
                    },
                });
                // 等待麦克风期间已松手：停止轨道并退出，不进入录音流程
                if (seq !== this._audioRecordSeq) {
                    this.audioH5Stream
                        .getTracks()
                        .forEach((track) => track.stop());
                    this.audioH5Stream = null;
                    return;
                }
                this.audioH5Source =
                    this.audioH5RecordCtx.createMediaStreamSource(
                        this.audioH5Stream
                    );
                // ScriptProcessorNode采集原始PCM数据
                const bufferSize = 4096;
                this.audioH5Processor =
                    this.audioH5RecordCtx.createScriptProcessor(
                        bufferSize,
                        1,
                        1
                    );
                this.audioH5PcmData = [];
                this.audioH5Processor.onaudioprocess = (e) => {
                    if (!this.audioIsRecording) return;
                    const pcm = e.inputBuffer.getChannelData(0);
                    this.audioH5PcmData.push(new Float32Array(pcm));
                };
                this.audioH5Source.connect(this.audioH5Processor);
                this.audioH5Processor.connect(
                    this.audioH5RecordCtx.destination
                );
            } catch (err) {
                console.warn('[audio] 获取麦克风失败:', err);
                // 仅当仍是当前这次录音时才复位状态（避免误清后一次的状态）
                if (seq === this._audioRecordSeq) {
                    this.audioCancelRecord();
                    this.$toast('无法获取麦克风，请检查权限');
                    // 权限被拒绝时弹回键盘模式，避免停留在无法使用的语音模式
                    this.chatIsVoiceMode = false;
                }
            }
        },

        // 停止录音并上传识别（序号+1 使迟到的麦克风启动作废）
        audioStopRecord() {
            if (!this.audioIsRecording) return;
            this.audioIsRecording = false;
            this._audioRecordSeq = (this._audioRecordSeq || 0) + 1;
            clearInterval(this.audioRecordTimer);
            this._h5OnRecordStop();
        },

        // 取消录音（丢弃已录内容，序号+1 使迟到的麦克风启动作废）
        audioCancelRecord() {
            if (!this.audioIsRecording) return;
            this.audioIsRecording = false;
            this._audioRecordSeq = (this._audioRecordSeq || 0) + 1;
            clearInterval(this.audioRecordTimer);
            this._h5CleanStream();
        },

        // 录音停止后：合并PCM数据并编码为WAV
        _h5OnRecordStop() {
            if (this.audioH5PcmData.length === 0) {
                // 麦克风未就绪或音频管线被挂起时无数据，明确告知用户而非静默
                this._h5CleanStream();
                this.$toast('未采集到声音，请再试一次');
                return;
            }
            // 合并所有PCM片段
            const totalLength = this.audioH5PcmData.reduce(
                (acc, buf) => acc + buf.length,
                0
            );
            const merged = new Float32Array(totalLength);
            let offset = 0;
            for (const chunk of this.audioH5PcmData) {
                merged.set(chunk, offset);
                offset += chunk.length;
            }
            // 将PCM从浏览器实际采样率降采样到16000Hz（阿里云ASR要求）
            const origSampleRate = this.audioH5RecordCtx
                ? this.audioH5RecordCtx.sampleRate
                : 44100;
            const targetSampleRate = 16000;
            const resampled = this._downsample(
                merged,
                origSampleRate,
                targetSampleRate
            );
            const wavBlob = this._encodeWav(resampled, targetSampleRate);
            this._h5CleanStream();
            const file = new File([wavBlob], 'recording.wav', {
                type: 'audio/wav',
            });
            this.audioUploadAudio(file);
        },

        // 降采样：将PCM从origRate转换到targetRate（线性插值）
        _downsample(buffer, origRate, targetRate) {
            if (origRate === targetRate) return buffer;
            const ratio = origRate / targetRate;
            const newLength = Math.round(buffer.length / ratio);
            const result = new Float32Array(newLength);
            for (let i = 0; i < newLength; i++) {
                const srcIdx = i * ratio;
                const idx = Math.floor(srcIdx);
                const frac = srcIdx - idx;
                if (idx + 1 < buffer.length) {
                    result[i] =
                        buffer[idx] * (1 - frac) + buffer[idx + 1] * frac;
                } else {
                    result[i] = buffer[idx];
                }
            }
            return result;
        },

        // 将PCM Float32数据编码为WAV格式的Blob
        _encodeWav(samples, sampleRate) {
            const buffer = new ArrayBuffer(44 + samples.length * 2);
            const view = new DataView(buffer);
            // WAV文件头
            this._writeString(view, 0, 'RIFF');
            view.setUint32(4, 36 + samples.length * 2, true);
            this._writeString(view, 8, 'WAVE');
            this._writeString(view, 12, 'fmt ');
            view.setUint32(16, 16, true); // PCM
            view.setUint16(20, 1, true); // PCM格式
            view.setUint16(22, 1, true); // 单声道
            view.setUint32(24, sampleRate, true);
            view.setUint32(28, sampleRate * 2, true); // 字节率
            view.setUint16(32, 2, true); // 块对齐
            view.setUint16(34, 16, true); // 16位
            this._writeString(view, 36, 'data');
            view.setUint32(40, samples.length * 2, true);
            // 写入PCM采样数据（Float32转Int16）
            let pos = 44;
            for (let i = 0; i < samples.length; i++, pos += 2) {
                const s = Math.max(-1, Math.min(1, samples[i]));
                view.setInt16(pos, s < 0 ? s * 0x8000 : s * 0x7fff, true);
            }
            return new Blob([buffer], { type: 'audio/wav' });
        },

        // 向DataView写入字符串（WAV头专用）
        _writeString(view, offset, str) {
            for (let i = 0; i < str.length; i++) {
                view.setUint8(offset + i, str.charCodeAt(i));
            }
        },

        // 清理录音资源（关闭音频上下文、停止麦克风）
        _h5CleanStream() {
            if (this.audioH5Processor) {
                this.audioH5Processor.disconnect();
                this.audioH5Processor.onaudioprocess = null;
                this.audioH5Processor = null;
            }
            if (this.audioH5Source) {
                this.audioH5Source.disconnect();
                this.audioH5Source = null;
            }
            if (this.audioH5RecordCtx) {
                this.audioH5RecordCtx.close().catch(() => {});
                this.audioH5RecordCtx = null;
            }
            if (this.audioH5Stream) {
                this.audioH5Stream.getTracks().forEach((track) => track.stop());
                this.audioH5Stream = null;
            }
            this.audioH5PcmData = [];
        },

        // 上传录音文件并进行语音识别，识别成功后继续对话流程
        async audioUploadAudio(filePath) {
            if (this.audioRecordTime < 1) {
                this.$toast('录音时间太短');
                return;
            }
            const thinkingIndex = this.messages.length;
            this.messages.push({ type: 'thinking' });
            this.chatScrollToBottom();

            try {
                const res = await uploadFile(
                    '/api/upload-audio-asr',
                    filePath,
                    'audio'
                );
                const data = res.data;
                if (data.code === 200 && data.data.text) {
                    this.$set(this.messages, thinkingIndex, {
                        type: 'user',
                        content: data.data.text,
                    });
                    this.chatScrollToBottom();
                    if (this.isAssessmentMode) {
                        this.assessmentSubmitAnswer(data.data.text);
                    } else {
                        this.chatSendToBackend(
                            data.data.text,
                            data.data.audioFile
                        );
                    }
                } else {
                    this.messages.splice(thinkingIndex, 1);
                    this.$toast(data.message || '语音识别失败');
                }
            } catch (err) {
                console.error('[audio] 上传录音失败:', err);
                // 401 踢下线场景：request.js 已统一弹提示并跳登录页，此处静默
                if (err.isAuthError) return;
                this.messages.splice(thinkingIndex, 1);
                this.$toast('上传失败，请重试');
            }
        },

        // 播放音频（音频真正出声时才显示说话动画，避免gif先动声音后响）
        audioPlay(audioUrl, onEnded) {
            const fullUrl = `${config.baseUrl}${audioUrl}`;

            if (!this.audioEl) {
                console.warn('[audio] audioEl未初始化');
                return;
            }

            // 新一次播放：作废上一次未完成的出声轮询
            this._audioSpeakToken = (this._audioSpeakToken || 0) + 1;

            this.audioEl.onended = null;
            this.audioEl.onerror = null;

            this.audioEl.onended = () => {
                console.log('[audio] 播放结束');
                this.isSpeaking = false;
                if (onEnded) onEnded();
            };
            this.audioEl.onerror = (e) => {
                const target = e.target || {};
                const errCode = target.error ? target.error.code : 0;
                console.error(
                    '[audio] 播放出错, code:',
                    errCode,
                    'src:',
                    target.src
                );
                this._audioSpeakToken++; // 作废出声轮询，避免出错后误点亮gif
                this.isSpeaking = false;
            };

            this.audioEl.src = fullUrl;
            this.audioEl.load();

            const playPromise = this.audioEl.play();
            if (playPromise && typeof playPromise.then === 'function') {
                playPromise
                    .then(() => {
                        // play成功≠已出声（浏览器还需激活音频管线/解码，iOS有数百毫秒会话激活延迟）
                        this._audioMarkSpeakingWhenAudible();
                    })
                    .catch((err) => {
                        console.warn(
                            '[audio] 播放被阻止:',
                            err.name,
                            err.message
                        );
                        this.audioPendingPlay = { audioUrl, onEnded };
                    });
            } else {
                // 不支持playPromise的旧浏览器：同样等待出声信号兜底
                this._audioMarkSpeakingWhenAudible();
            }
        },

        // 轮询音频时钟：currentTime真正推进（即将/正在出声）才点亮说话动画
        // 比play成功回调更接近实际出声时刻，超时600ms兜底防止个别浏览器时钟不推进
        _audioMarkSpeakingWhenAudible() {
            const el = this.audioEl;
            const token = (this._audioSpeakToken =
                (this._audioSpeakToken || 0) + 1);
            const startAt = Date.now();
            const check = () => {
                // token变化说明已切歌/停止/出错，放弃本次点亮
                if (token !== this._audioSpeakToken) return;
                if (el.currentTime > 0 || Date.now() - startAt > 600) {
                    this.isSpeaking = true;
                    return;
                }
                requestAnimationFrame(check);
            };
            check();
        },

        // 停止音频播放
        audioStop() {
            this._audioSpeakToken = (this._audioSpeakToken || 0) + 1; // 作废出声轮询
            if (this.audioEl) {
                this.audioEl.onended = null;
                this.audioEl.onerror = null;
                this.audioEl.pause();
                this.audioEl.currentTime = 0;
            }
            this.isSpeaking = false;
        },

        // 语音合成并播放
        async audioSynthesizeAndPlay(text, callback) {
            try {
                const res = await request({
                    url: '/api/welcome-tts',
                    method: 'POST',
                    data: {
                        guideData: {
                            stage: '',
                            interests: [],
                            customWelcome: text,
                        },
                    },
                });
                if (res.data.code === 200 && res.data.data.audioUrl) {
                    this.audioPlay(res.data.data.audioUrl);
                }
            } catch (e) {
                console.error('语音合成失败:', e);
            }
            if (callback) callback();
        },
    },
};
